import { sanitizeQueryParams } from "@sushicorp/utils";
import { sanitizeText } from "@sushicorp/utils";
import { BaseProduct, Store } from "artisn/types";
import { GetStaticPaths, GetStaticProps } from "next";
import React from "react";
import { QueryClient } from "react-query";
import { dehydrate } from "react-query/hydration";

import Product from "components/product/Product/Product";
import CONSTANTS from "config/constants";
import { prefetchDefaultStore, prefetchProducts } from "utils/cache.utils";
import { prefetchProductDetails } from "utils/cache.utils";
import { notify } from "utils/common.utils";

const { ARTISN, API } = CONSTANTS;
const { DEFAULT_VENDOR, DEFAULT_CATALOGUE } = ARTISN;
const { PRODUCTS_PER_CATEGORY_REQUEST } = API;
const { catalogueId } = DEFAULT_CATALOGUE;

interface Props {}

const ProductPage = (props: Props) => {
  return <Product />;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const queryClient = new QueryClient();

  await prefetchDefaultStore(queryClient);
  const defaultStore = queryClient.getQueryData<Store>(["stores", "default"]);
  const storeId = defaultStore ? defaultStore.storeId : 0;

  let productList: BaseProduct[] = [];
  let hasMorePages = false;
  let page = 1;

  do {
    const productsPromise = prefetchProducts(queryClient, {
      storeId,
      size: PRODUCTS_PER_CATEGORY_REQUEST,
      page
    });

    await Promise.all([productsPromise]);

    const products = queryClient.getQueryData<any>([
      "products",
      {
        storeId,
        vendorId: DEFAULT_VENDOR.id,
        catalogueId: catalogueId,
        size: PRODUCTS_PER_CATEGORY_REQUEST,
        page: page
      }
    ]);
    const pageProducts = products?.pages?.[0].data;
    if (pageProducts) {
      productList = [...productList, ...pageProducts];
    }
    hasMorePages = products?.pages?.[0].hasMorePages;

    if (hasMorePages) page++;
  } while (hasMorePages);

  const paths = productList.map((product: BaseProduct) => {
    const { name, productId } = product;
    return {
      params: {
        productName: sanitizeText(name),
        productId: `${productId}`
      }
    };
  });

  return {
    paths: process.env.ARTISN_CAPACITOR === "true" ? paths : [],
    fallback: process.env.ARTISN_CAPACITOR !== "true"
  };
};

export const getStaticProps: GetStaticProps = async context => {
  const queryClient = new QueryClient();

  try {
    const { params: query = {} } = context;
    const { productId } = sanitizeQueryParams(query);

    if (!productId) return { props: {} };

    await prefetchDefaultStore(queryClient);
    const defaultStore = queryClient.getQueryData<Store>(["stores", "default"]);

    const products = prefetchProductDetails(queryClient, {
      storeId: defaultStore!.storeId,
      catalogueId,
      vendorId: DEFAULT_VENDOR.id,
      productId
    });

    await Promise.all([products]);
  } catch (e) {
    notify(e, "Prefetch product details");
    console.error(e.message);
  }

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient)))
    },
    revalidate: 60 * 60
  };
};

export default ProductPage;
